import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Filter, Eye, Download, Plus, Edit, Building, Factory, Home, AlertCircle, CheckCircle } from 'lucide-react';
import StatCard from '../components/Dashboard/StatCard';
import InteractiveMap from '../components/Map/InteractiveMap';
import PageHeader from '../components/Common/PageHeader';
import FormPageShell from '../components/Common/FormPageShell';
import QueryErrorBanner from '../components/Common/QueryErrorBanner';
import FormModalLayout from '../components/Common/FormModalLayout';
import Can from '../components/Common/Can';
import { identityService, licenseService, complaintService, fiscalizacaoService, dashboardService, routingService } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { parseGeoJSONInput } from '../utils/geoBuffer';
import { searchAddressNominatim } from '../utils/geocodeNominatim';
import { MAP_OVERLAYS } from '../utils/mapOverlayLayers';
import { eventTimeMs } from '../utils/mapMeasure';
import toast from 'react-hot-toast';
import { userFacingError } from '../i18n/userFacingError';
import { getAppHomePath } from '../constants/navigation';
import { P } from '../constants/permissions';
import { useApiQuery } from '../hooks/useApi';
import {
  mergeLicenseStatusForEnterprises,
  countExpiredEmitidas,
} from '../utils/enterpriseLicenseMap';
import { fetchAllPages } from '../utils/apiPagination';
import { useTranslation } from 'react-i18next';

const Map = () => {
  const { t } = useTranslation('map');
  const { hasPermission } = usePermissions();
  const canIdentity = hasPermission(P.IDENTITY_ACCESS);
  const canEnterprises = canIdentity || hasPermission(P.ENTERPRISES_VIEW) || hasPermission(P.MAP_VIEW);
  const canComplaints = hasPermission(P.COMPLAINT_ACCESS);

  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    region: ''
  });

  const [viewMode, setViewMode] = useState('map');
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [radiusM, setRadiusM] = useState(500);
  const [overlap, setOverlap] = useState(null);
  const [overlapOrigin, setOverlapOrigin] = useState(null);
  const [showExpired, setShowExpired] = useState(true);
  const [clusterEnabled, setClusterEnabled] = useState(true);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [overlayIds, setOverlayIds] = useState([]);
  const [clickMode, setClickMode] = useState('select');
  const [measureType, setMeasureType] = useState(null);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressHits, setAddressHits] = useState([]);
  const [flyTo, setFlyTo] = useState(null);
  const [routeStops, setRouteStops] = useState([]);
  const [routeResult, setRouteResult] = useState(null);
  const [timeMax, setTimeMax] = useState(null);
  const [protectedHit, setProtectedHit] = useState(null);
  const [protectedLoading, setProtectedLoading] = useState(false);
  const [areaForm, setAreaForm] = useState({ nome: '', tipo: 'uc', geojson: '' });

  const { data: entPayload, isLoading: entLoading } = useApiQuery(
    'map-page-enterprises',
    () => fetchAllPages((params) => identityService.getEnterprises(params)),
    { enabled: canEnterprises }
  );
  const { data: licPayload, isLoading: licLoading } = useApiQuery('map-page-licencas', () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const { data: denPayload, isError: denError, error: denErr, refetch: refetchDen } = useApiQuery(
    'map-page-complaints',
    () => fetchAllPages((params) => complaintService.getComplaints(params)),
    { enabled: canComplaints }
  );
  const { data: vistPayload, isError: vistError, error: vistErr, refetch: refetchVist } = useApiQuery(
    'map-page-vistorias',
    () => fetchAllPages((params) => fiscalizacaoService.listVistorias(params)),
    { enabled: canComplaints }
  );
  const { data: geoRaw, isError: geoError, error: geoErr, refetch: refetchGeo } = useApiQuery(
    ['map-geojson', radiusM],
    () => dashboardService.getGeoJson({ radius_m: radiusM }).then((r) => r.data)
  );
  const { data: areasRaw, refetch: refetchAreas } = useApiQuery(
    'map-areas-protegidas',
    () => identityService.listAreasProtegidas().then((r) => r.data),
    { enabled: canIdentity }
  );
  const areasProtegidas = Array.isArray(areasRaw) ? areasRaw : areasRaw?.data ?? [];

  const isLoading = entLoading || licLoading;

  const enterprises = useMemo(() => {
    const raw = Array.isArray(entPayload) ? entPayload : entPayload?.data ?? entPayload?.items ?? [];
    const licRaw = Array.isArray(licPayload) ? licPayload : licPayload?.data ?? licPayload?.items ?? [];
    const healthByEmp = mergeLicenseStatusForEnterprises(licRaw);
    const expiredCount = countExpiredEmitidas(licRaw);

    return {
      list: (raw || []).map((enterprise) => {
        const typeRaw = (enterprise.type || 'default').toString();
        const typeLower = typeRaw.toLowerCase();
        return {
          id: enterprise.id,
          name: enterprise.name || enterprise.nome,
          type: typeLower,
          is_active: enterprise.status === 'ACTIVE' || enterprise.is_active !== false,
          description: enterprise.description,
          address: enterprise.address,
          city: enterprise.city,
          state: enterprise.state,
          latitude: enterprise.latitude,
          longitude: enterprise.longitude,
          created_at: enterprise.created_at,
          license_status: healthByEmp[enterprise.id] ?? 'none',
          eventDate: enterprise.created_at,
          area_geojson: enterprise.area_geojson,
        };
      }),
      expiredLicensesCount: expiredCount,
    };
  }, [entPayload, licPayload]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const complaintMarkers = useMemo(() => {
    const raw = Array.isArray(denPayload) ? denPayload : denPayload?.data ?? [];
    return (raw || [])
      .filter((row) => row.latitude != null && row.longitude != null)
      .map((row) => ({
        id: `denuncia-${row.id}`,
        name: row.local || 'Denúncia',
        type: 'complaint',
        is_active: true,
        latitude: row.latitude,
        longitude: row.longitude,
        license_status: 'none',
        city: row.cidade,
        eventDate: row.data || row.created_at,
      }));
  }, [denPayload]);

  const expiredMarkers = useMemo(() => {
    const feats = geoRaw?.features || [];
    return feats
      .filter((f) => f.properties?.layer === 'expired_license' && f.geometry?.coordinates)
      .map((f) => ({
        id: `expired-${f.properties.id}`,
        name: f.properties.name || 'Licença vencida',
        type: 'expired_license',
        is_active: false,
        latitude: f.geometry.coordinates[1],
        longitude: f.geometry.coordinates[0],
        license_status: 'expired',
        eventDate: f.properties.validade,
      }));
  }, [geoRaw]);

  const vistoriaMarkers = useMemo(() => {
    const raw = Array.isArray(vistPayload) ? vistPayload : vistPayload?.data ?? [];
    return (raw || [])
      .filter((row) => row.latitude != null && row.longitude != null)
      .map((row) => ({
        id: `vistoria-${row.id}`,
        name: `Vistoria ${row.license_tipo || ''}`.trim(),
        type: 'vistoria',
        is_active: true,
        latitude: row.latitude,
        longitude: row.longitude,
        license_status: 'none',
        eventDate: row.created_at,
      }));
  }, [vistPayload]);

  const filteredEnterprises = [
    ...enterprises.list.filter((enterprise) => {
      if (filters.status && enterprise.is_active !== (filters.status === 'active')) return false;
      if (filters.type && enterprise.type !== filters.type) return false;
      if (filters.region && !enterprise.city?.toLowerCase().includes(filters.region.toLowerCase())) return false;
      return true;
    }),
    ...complaintMarkers,
    ...vistoriaMarkers,
    ...(showExpired ? expiredMarkers : []),
  ].filter((row) => {
    if (!timeMax) return true;
    const ms = eventTimeMs(row.eventDate);
    return ms == null || ms <= timeMax;
  });

  const timeBounds = useMemo(() => {
    const times = [
      ...enterprises.list,
      ...complaintMarkers,
      ...vistoriaMarkers,
      ...expiredMarkers,
    ]
      .map((row) => eventTimeMs(row.eventDate))
      .filter((ms) => ms != null);
    if (!times.length) return null;
    return { min: Math.min(...times), max: Math.max(...times) };
  }, [enterprises.list, complaintMarkers, vistoriaMarkers, expiredMarkers]);

  const TYPE_LABELS = {
    industrial: 'Industrial',
    commercial: 'Comercial',
    residential: 'Residencial',
    agricultural: 'Agrícola',
    default: 'Empreendimento',
  };

  const handleEnterpriseClick = (enterprise) => {
    setSelectedEnterprise(enterprise);
  };

  const exportGeoJson = async () => {
    try {
      const { data } = await dashboardService.getGeoJson({ radius_m: radiusM });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/geo+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ambiental-mapa.geojson';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  const loadOverlap = async (lat, lng) => {
    try {
      setOverlapOrigin({ lat, lng });
      const { data } = await dashboardService.getGeoOverlap({ lat, lng, radius_m: radiusM });
      setOverlap(data);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  const geoCoverage = useMemo(() => {
    const list = enterprises.list || [];
    const withGeo = list.filter((e) => e.latitude != null && e.longitude != null).length;
    const pct = list.length ? Math.round((100 * withGeo) / list.length) : 0;
    const byCity = {};
    for (const e of list) {
      const key = e.city || 'Sem município';
      if (!byCity[key]) byCity[key] = { total: 0, geo: 0 };
      byCity[key].total += 1;
      if (e.latitude != null && e.longitude != null) byCity[key].geo += 1;
    }
    return { withGeo, total: list.length, pct, byCity };
  }, [enterprises.list]);

  const getStatusIcon = (isActive) => {
    return isActive ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <AlertCircle className="w-5 h-5 text-red-500" />
    );
  };

  const searchAddress = async () => {
    try {
      const hits = await searchAddressNominatim(addressQuery, { countrycodes: 'br' });
      setAddressHits(hits);
      if (!hits.length) toast.error('Nenhum endereço encontrado.');
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  const toggleOverlay = (id) => {
    setOverlayIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const checkProtected = async (enterpriseId) => {
    if (!enterpriseId) return;
    setProtectedLoading(true);
    try {
      const r = await dashboardService.getProtectedIntersect(enterpriseId);
      setProtectedHit(r.data);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    } finally {
      setProtectedLoading(false);
    }
  };

  const saveAreaProtegida = async () => {
    let geo;
    try {
      geo = parseGeoJSONInput(areaForm.geojson);
    } catch {
      toast.error('GeoJSON inválido. Use Polygon ou MultiPolygon.');
      return;
    }
    if (!areaForm.nome.trim() || !geo) {
      toast.error('Informe o nome e o polígono da área.');
      return;
    }
    try {
      await identityService.createAreaProtegida({
        nome: areaForm.nome.trim(),
        tipo: areaForm.tipo || 'uc',
        geojson: geo,
      });
      toast.success('Área protegida cadastrada.');
      setAreaForm({ nome: '', tipo: 'uc', geojson: '' });
      refetchAreas();
    } catch (e) {
      toast.error(userFacingError(e, 'requestError'));
    }
  };

  const calcMapRoute = async () => {
    if (routeStops.length < 2) {
      toast.error('Inclua pelo menos duas paradas no mapa.');
      return;
    }
    try {
      const { data } = await routingService.orderTrip(
        routeStops.map((s, i) => ({
          id: String(s.id || i),
          label: s.label || `Parada ${i + 1}`,
          latitude: Number(s.lat),
          longitude: Number(s.lng),
          kind: 'map',
        })),
        { include_geometry: true }
      );
      setRouteResult(data);
      toast.success('Rota calculada no OSRM.');
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      industrial: <Factory className="w-5 h-5 text-blue-500" />,
      commercial: <Building className="w-5 h-5 text-purple-500" />,
      residential: <Home className="w-5 h-5 text-green-500" />,
      agricultural: <MapPin className="w-5 h-5 text-yellow-500" />,
      default: <MapPin className="w-5 h-5 text-gray-500" />
    };
    return icons[type] || icons.default;
  };

  return (
    <div>
      <FormPageShell maxWidth="max-w-content" className="w-full px-0 pb-4 pt-0">
        <div className="space-y-6">
          <PageHeader
            title={t("title")}
            description="Visualize empreendimentos e licenças ambientais"
            breadcrumbs={[
              { label: 'Início', to: getAppHomePath() },
              { label: 'Mapa' },
            ]}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Can permission={P.MONITORING_ACCESS}>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  Raio (m)
                  <input
                    type="number"
                    min={50}
                    max={50000}
                    className="form-input w-24"
                    value={radiusM}
                    onChange={(e) => setRadiusM(Number(e.target.value) || 500)}
                  />
                </label>
                <button type="button" className="btn btn-outline shadow-sm" onClick={exportGeoJson}>
                  <Download className="mr-2 h-4 w-4" aria-hidden />
                  Exportar GeoJSON
                </button>
              </Can>
              <Can any={[P.IDENTITY_ACCESS, P.ENTERPRISES_VIEW]}>
                <button
                  type="button"
                  className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
                  onClick={() => navigate('/enterprises', { state: { openEnterpriseCreate: true } })}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Novo empreendimento
                </button>
              </Can>
            </div>
          </PageHeader>
          {denError || vistError || geoError ? (
            <div className="space-y-2">
              {denError ? <QueryErrorBanner error={denErr} onRetry={() => refetchDen()} /> : null}
              {vistError ? <QueryErrorBanner error={vistErr} onRetry={() => refetchVist()} /> : null}
              {geoError ? <QueryErrorBanner error={geoErr} onRetry={() => refetchGeo()} /> : null}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <StatCard
          title={t("totalEnterprises")}
          value={enterprises.list.length}
          icon={MapPin}
        />
        <StatCard
          title={t("active")}
          value={enterprises.list.filter((e) => e.is_active).length}
          icon={Eye}
        />
        <StatCard
          title={t("inactive")}
          value={enterprises.list.filter((e) => !e.is_active).length}
          icon={Filter}
        />
        <StatCard
          title={t("expiredLicenses")}
          value={enterprises.expiredLicensesCount}
          icon={Edit}
        />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase text-gray-500">Cobertura georreferenciada</p>
              <p className="text-2xl font-semibold text-gray-900">{geoCoverage.pct}%</p>
              <p className="text-sm text-gray-600">
                {geoCoverage.withGeo} de {geoCoverage.total} empreendimentos com coordenadas
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase text-gray-500">Por município</p>
              <ul className="mt-2 max-h-28 space-y-1 overflow-auto text-sm text-gray-700">
                {Object.entries(geoCoverage.byCity)
                  .sort((a, b) => b[1].total - a[1].total)
                  .slice(0, 8)
                  .map(([city, row]) => (
                    <li key={city} className="flex justify-between gap-2">
                      <span>{city}</span>
                      <span className="text-gray-500">
                        {row.geo}/{row.total}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <div className="data-list-card sticky top-0 z-20">
            <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
              <div className="mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary-700" aria-hidden />
                <h2 className="text-base font-semibold tracking-tight text-gray-900">Filtros</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="form-group mb-0">
                  <label htmlFor="map-filter-status" className="form-label">
                    Status
                  </label>
                  <select
                    id="map-filter-status"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Todos os status</option>
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div className="form-group mb-0">
                  <label htmlFor="map-filter-type" className="form-label">
                    Tipo
                  </label>
                  <select
                    id="map-filter-type"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="industrial">Industrial</option>
                    <option value="commercial">Comercial</option>
                    <option value="agricultural">Agrícola</option>
                    <option value="residential">Residencial</option>
                  </select>
                </div>
                <div className="form-group mb-0">
                  <label htmlFor="map-filter-region" className="form-label">
                    Região
                  </label>
                  <input
                    id="map-filter-region"
                    type="text"
                    value={filters.region}
                    onChange={(e) => handleFilterChange('region', e.target.value)}
                    className="form-input"
                    placeholder="Cidade ou região..."
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className={`btn ${viewMode === 'map' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Mapa
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Lista
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Mostrando {filteredEnterprises.length} de {enterprises.list.length} empreendimentos
              </div>
            </div>
          </div>
        </div>
      </FormPageShell>

      {viewMode === 'map' ? (
        <div className="border-t border-gray-200 bg-white">
          {isLoading ? (
            <div className="flex min-h-[480px] items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                <p className="text-gray-600">Carregando mapa…</p>
              </div>
            </div>
          ) : (
            <div className="px-0 pb-10 pt-4">
              {overlap ? (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Sobreposição em {overlap.radius_m} m: {overlap.overlap_count} ponto(s)
                  {' · '}
                  {overlap.enterprises?.length || 0} empreendimento(s), {overlap.complaints?.length || 0} denúncia(s),{' '}
                  {overlap.vistorias?.length || 0} vistoria(s), {overlap.expired_licenses?.length || 0} licença(s) vencida(s).
                </div>
              ) : (
                <p className="mb-3 text-xs text-gray-500">Clique no mapa para ver sobreposição no raio configurado.</p>
              )}
              <div className="mb-3 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-wrap items-end gap-2">
                  <label className="form-group mb-0 min-w-[16rem] flex-1">
                    <span className="form-label">Buscar endereço</span>
                    <input
                      className="form-input"
                      value={addressQuery}
                      onChange={(e) => setAddressQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          searchAddress();
                        }
                      }}
                      placeholder="Rua, bairro ou município…"
                    />
                  </label>
                  <button type="button" className="btn btn-primary btn-sm" onClick={searchAddress}>
                    Buscar
                  </button>
                </div>
                {addressHits.length ? (
                  <ul className="max-h-28 space-y-1 overflow-auto text-sm">
                    {addressHits.map((hit) => (
                      <li key={`${hit.lat}-${hit.lon}`}>
                        <button
                          type="button"
                          className="text-left text-primary-700 hover:underline"
                          onClick={() => {
                            setFlyTo(hit);
                            setAddressHits([]);
                          }}
                        >
                          {hit.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="form-check" checked={clusterEnabled} onChange={(e) => setClusterEnabled(e.target.checked)} />
                    Cluster
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="form-check" checked={heatmapEnabled} onChange={(e) => setHeatmapEnabled(e.target.checked)} />
                    Heatmap
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="form-check" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
                    Licenças vencidas
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  {MAP_OVERLAYS.map((layer) => (
                    <label key={layer.id} className="flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs">
                      <input
                        type="checkbox"
                        className="form-check"
                        checked={overlayIds.includes(layer.id)}
                        onChange={() => toggleOverlay(layer.id)}
                      />
                      {layer.label}
                    </label>
                  ))}
                </div>
                <Can permission={P.IDENTITY_ACCESS}>
                  <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                    <p className="text-sm font-medium text-gray-800">Áreas protegidas (interseção persistida)</p>
                    {areasProtegidas.length ? (
                      <ul className="max-h-24 space-y-1 overflow-auto text-xs text-gray-600">
                        {areasProtegidas.map((area) => (
                          <li key={area.id} className="flex items-center justify-between gap-2">
                            <span>
                              {area.nome} · {area.tipo}
                            </span>
                            <button
                              type="button"
                              className="text-red-600 hover:underline"
                              onClick={async () => {
                                try {
                                  await identityService.deleteAreaProtegida(area.id);
                                  refetchAreas();
                                } catch (e) {
                                  toast.error(userFacingError(e, 'requestError'));
                                }
                              }}
                            >
                              Excluir
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-500">Nenhuma área cadastrada neste órgão.</p>
                    )}
                    <div className="grid gap-2 md:grid-cols-3">
                      <input
                        className="form-input"
                        placeholder="Nome da UC / APP / zona"
                        value={areaForm.nome}
                        onChange={(e) => setAreaForm((f) => ({ ...f, nome: e.target.value }))}
                      />
                      <select
                        className="form-select"
                        value={areaForm.tipo}
                        onChange={(e) => setAreaForm((f) => ({ ...f, tipo: e.target.value }))}
                      >
                        <option value="uc">UC</option>
                        <option value="app">APP</option>
                        <option value="zona">Zona</option>
                        <option value="embargo">Embargo</option>
                      </select>
                      <button type="button" className="btn btn-outline btn-sm" onClick={saveAreaProtegida}>
                        Cadastrar área
                      </button>
                    </div>
                    <textarea
                      className="form-input font-mono text-xs"
                      rows={3}
                      placeholder='GeoJSON Polygon, ex.: {"type":"Polygon","coordinates":[[[...]]]}'
                      value={areaForm.geojson}
                      onChange={(e) => setAreaForm((f) => ({ ...f, geojson: e.target.value }))}
                    />
                  </div>
                </Can>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-gray-500">Ferramenta</span>
                  {[
                    ['select', 'Selecionar'],
                    ['route', 'Rota'],
                    ['measure', 'Medir'],
                  ].map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      className={`btn btn-sm ${clickMode === mode ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => {
                        setClickMode(mode);
                        if (mode === 'measure' && !measureType) setMeasureType('line');
                        if (mode !== 'measure') setMeasureType(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                  {clickMode === 'measure' ? (
                    <>
                      <button
                        type="button"
                        className={`btn btn-sm ${measureType === 'line' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setMeasureType('line')}
                      >
                        Distância
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${measureType === 'area' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setMeasureType('area')}
                      >
                        Área
                      </button>
                    </>
                  ) : null}
                </div>
                {timeBounds ? (
                  <label className="block text-sm text-gray-700">
                    Até {new Date(timeMax || timeBounds.max).toLocaleDateString('pt-BR')}
                    <input
                      type="range"
                      className="mt-1 w-full"
                      min={timeBounds.min}
                      max={timeBounds.max}
                      value={timeMax || timeBounds.max}
                      onChange={(e) => setTimeMax(Number(e.target.value))}
                    />
                  </label>
                ) : null}
                {clickMode === 'route' ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>{routeStops.length} parada(s)</span>
                    <button type="button" className="btn btn-primary btn-sm" onClick={calcMapRoute}>
                      Calcular rota
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => {
                        setRouteStops([]);
                        setRouteResult(null);
                      }}
                    >
                      Limpar rota
                    </button>
                    {routeResult?.distance_m != null ? (
                      <span className="text-gray-600">
                        {(routeResult.distance_m / 1000).toFixed(1)} km
                        {routeResult.duration_s != null ? ` · ${Math.round(routeResult.duration_s / 60)} min` : ''}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="relative h-[70vh] min-h-[420px]">
              <InteractiveMap
                enterprises={filteredEnterprises}
                onFeatureSelect={handleEnterpriseClick}
                onMapClick={clickMode === 'select' ? loadOverlap : undefined}
                onRouteStop={(stop) => setRouteStops((prev) => [...prev, stop])}
                radiusCenter={overlapOrigin}
                radiusM={overlapOrigin && clickMode === 'select' ? radiusM : 0}
                clusterEnabled={clusterEnabled}
                heatmapEnabled={heatmapEnabled}
                overlayIds={overlayIds}
                clickMode={clickMode}
                measureType={measureType}
                routeGeometry={routeResult?.geometry}
                flyTo={flyTo}
                onEnterpriseClick={(enterprise) => {
                  if (!enterprise?.id) return;
                  if (String(enterprise.id).startsWith('vistoria-')) {
                    navigate('/inspections');
                    return;
                  }
                  if (String(enterprise.id).startsWith('denuncia-')) {
                    navigate('/complaints');
                    return;
                  }
                  if (String(enterprise.id).startsWith('expired-')) {
                    navigate('/licenses');
                    return;
                  }
                  navigate('/enterprises', { state: { openEnterpriseId: enterprise.id } });
                }}
                center={[-35.7353, -9.6658]}
                zoom={12}
                height="100%"
                mapClassName="h-full rounded-xl border-gray-200 shadow-soft"
                colorByLicenseStatus
              />
              </div>
              <p className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                <span className="inline-block h-3 w-5 rounded-sm border-2 border-teal-700 bg-teal-500/30" aria-hidden />
                Área do empreendimento (polígono cadastrado)
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mx-auto w-full pb-6">
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Localização
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEnterprises.map((enterprise) => (
                  <tr key={enterprise.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {enterprise.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        enterprise.type === 'industrial' 
                          ? 'bg-blue-100 text-blue-700' 
                          : enterprise.type === 'commercial'
                          ? 'bg-purple-100 text-purple-700'
                          : enterprise.type === 'agricultural'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {enterprise.type === 'industrial' ? 'Industrial' :
                         enterprise.type === 'commercial' ? 'Comercial' :
                         enterprise.type === 'agricultural' ? 'Agrícola' : 'Residencial'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        enterprise.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {enterprise.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {enterprise.city && enterprise.state ? `${enterprise.city}, ${enterprise.state}` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Can permission={P.MONITORING_ACCESS}>
                          <button type="button" className="text-primary-600 hover:text-primary-900">
                            <Eye className="w-4 h-4" />
                          </button>
                        </Can>
                        <Can permission={P.IDENTITY_ACCESS}>
                          <button type="button" className="text-gray-600 hover:text-gray-900">
                            <Edit className="w-4 h-4" />
                          </button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}

      {selectedEnterprise && (
        <FormModalLayout
          title={selectedEnterprise.name}
          subtitle={TYPE_LABELS[selectedEnterprise.type] || TYPE_LABELS.default}
          headerIcon={MapPin}
          onClose={() => setSelectedEnterprise(null)}
          size="2xl"
          historyEntityType={selectedEnterprise.type === 'complaint' ? 'complaints' : 'enterprises'}
          historyEntityId={selectedEnterprise.id}
          footer={
            <>
              <button type="button" onClick={() => setSelectedEnterprise(null)} className="btn btn-outline">
                Fechar
              </button>
              <Can permission={P.MONITORING_ACCESS}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    if (!selectedEnterprise?.id) return;
                    navigate('/enterprises', { state: { openEnterpriseId: selectedEnterprise.id } });
                    setSelectedEnterprise(null);
                  }}
                >
                  Ver detalhes completos
                </button>
              </Can>
            </>
          }
        >
          <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
            {getTypeIcon(selectedEnterprise.type)}
            <div className="flex items-center gap-2">
              {getStatusIcon(selectedEnterprise.is_active)}
              <span
                className={`text-sm font-medium ${
                  selectedEnterprise.is_active ? 'text-green-700' : 'text-red-700'
                }`}
              >
                {selectedEnterprise.is_active ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-5">
              {selectedEnterprise.description ? (
                <div>
                  <h3 className="form-section-title">Descrição</h3>
                  <p className="text-sm leading-relaxed text-gray-600">{selectedEnterprise.description}</p>
                </div>
              ) : null}
              {selectedEnterprise.address ? (
                <div>
                  <h3 className="form-section-title">Endereço</h3>
                  <p className="text-sm text-gray-600">{selectedEnterprise.address}</p>
                </div>
              ) : null}
            </div>
            <div className="space-y-5">
              {selectedEnterprise.city && selectedEnterprise.state ? (
                <div>
                  <h3 className="form-section-title">Localização</h3>
                  <p className="text-sm text-gray-600">
                    {selectedEnterprise.city}, {selectedEnterprise.state}
                  </p>
                </div>
              ) : null}
              {selectedEnterprise.latitude != null && selectedEnterprise.longitude != null ? (
                <div>
                  <h3 className="form-section-title">Coordenadas</h3>
                  <p className="text-sm font-mono text-gray-600">
                    {Number(selectedEnterprise.latitude).toFixed(6)}, {Number(selectedEnterprise.longitude).toFixed(6)}
                  </p>
                </div>
              ) : null}
              <div>
                <h3 className="form-section-title">Área protegida</h3>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={protectedLoading}
                  onClick={() => checkProtected(selectedEnterprise.id)}
                >
                  {protectedLoading ? 'Verificando…' : 'Intersecta área protegida?'}
                </button>
                {protectedHit && String(protectedHit.enterprise_id) === String(selectedEnterprise.id) ? (
                  <p className={`mt-2 text-sm ${protectedHit.intersects ? 'text-red-700' : 'text-green-700'}`}>
                    {protectedHit.intersects
                      ? `Sim — ${protectedHit.areas?.length || 0} área(s): ${(protectedHit.areas || [])
                          .map((a) => a.nome)
                          .join(', ')}`
                      : 'Não intersecta área protegida nem embargo cadastrado.'}
                  </p>
                ) : null}
              </div>
              {selectedEnterprise.created_at ? (
                <div>
                  <h3 className="form-section-title">Data de cadastro</h3>
                  <p className="text-sm text-gray-600">
                    {new Date(selectedEnterprise.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </FormModalLayout>
      )}
    </div>
  );
};

export default Map;